/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { ApolloError, gql, useMutation } from "@apollo/client";

import { useErrorsOrSuccessHandler } from "graphql/errorHandlers";
import {
  ACCOUNT_FRAGMENT,
  AccountFragment,
  ERROR_FRAGMENT,
  ErrorFragment,
} from "graphql/fragments";
import { SESSION, SessionQuery } from "graphql/queries";
import { CriiptoVerifyInput } from "graphql/types";

const CRIIPTO_VERIFY = gql`
  mutation ($input: CriiptoVerifyInput!) {
    criiptoVerify(input: $input) {
      account {
        ...AccountFragment
      }
      errors {
        ...ErrorFragment
      }
    }
  }
  ${ACCOUNT_FRAGMENT}
  ${ERROR_FRAGMENT}
`;

interface Payload {
  account?: AccountFragment | null;
  errors: ErrorFragment[];
}

interface CriiptoVerifyMutation {
  criiptoVerify: Payload | null;
}

export const useCriiptoVerifyMutation = () => {
  const handleErrors = useErrorsOrSuccessHandler(
    "success.criiptyVerified",
    "Your identity has been verified"
  );
  // Values for extraction
  // t("common:success.criiptyVerified", "Your identity has been verified")

  const [mutation, { data, loading }] = useMutation<
    CriiptoVerifyMutation,
    { input: CriiptoVerifyInput }
  >(CRIIPTO_VERIFY);

  const criiptoVerify = (
    input: CriiptoVerifyInput,
    onSuccess?: (account: AccountFragment) => void
  ) =>
    mutation({
      variables: { input },
      update(cache, { data: updatedData }) {
        const account = updatedData?.criiptoVerify?.account;
        if (!account) return;

        cache.modify({
          fields: {
            session() {
              const sessionQuery = cache.readQuery<SessionQuery>({
                query: SESSION,
              });
              if (!sessionQuery) return;

              cache.writeQuery<SessionQuery>({
                query: SESSION,
                data: {
                  session: {
                    ...sessionQuery.session,
                    account,
                    hasIdentity: Boolean(account.defaultIdentity),
                  },
                },
              });
            },
          },
        });
      },
    })
      .then(({ data, errors }) => {
        const dataErrors = data?.criiptoVerify?.errors ?? [];
        const account = data?.criiptoVerify?.account;

        handleErrors({
          dataErrors,
          graphqlErrors: errors,
          onSuccess: () => onSuccess && account && onSuccess(account),
        });
      })
      .catch((_error: ApolloError) => null);

  return {
    criiptoVerify,
    isLoading: loading,
    account: data?.criiptoVerify?.account,
    errors: data?.criiptoVerify?.errors ?? [],
  };
};
